import React, { useState, useCallback } from 'react';
import { GoogleMap, LoadScript, Marker, Polyline, InfoWindow } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const containerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '12px'
};

const defaultCenter = {
  lat: 33.5138,
  lng: 36.2765
};

const GoogleMapComponent = ({ 
  center = defaultCenter, 
  zoom = 13, 
  markers = [],
  pickup = null,
  dropoff = null,
  driverLocation = null,
  showRoute = false,
  height = '400px',
  onMapClick = null
}) => {
  const [map, setMap] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);

  const mapCenter = center?.lat ? { lat: center.lat || center[0], lng: center.lng || center[1] } : defaultCenter;

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Route coordinates
  const routeCoordinates = showRoute && pickup && dropoff 
    ? [
        { lat: pickup.lat, lng: pickup.lng },
        { lat: dropoff.lat, lng: dropoff.lng }
      ]
    : [];

  // Custom marker icons - only create when google is loaded
  const getPickupIcon = () => {
    if (!window.google) return null;
    return {
      url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIxNiIgY3k9IjE2IiByPSIxMiIgZmlsbD0iIzEwYjk4MSIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjIiLz48L3N2Zz4=',
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(16, 16)
    };
  };

  const getDropoffIcon = () => {
    if (!window.google) return null;
    return {
      url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIxNiIgY3k9IjE2IiByPSIxMiIgZmlsbD0iI2VmNDQ0NCIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjIiLz48L3N2Zz4=',
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(16, 16)
    };
  };

  const getDriverIcon = () => {
    if (!window.google) return null;
    return {
      url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMjAgNUMxNS4wMjk0IDUgMTEgOS4wMjk0NCAxMSAxNEMxMSAyMC4yNSAyMCAzMCAyMCAzMEMyMCAzMCAyOSAyMC4yNSAyOSAxNEMyOSA5LjAyOTQ0IDI0Ljk3MDYgNSAyMCA1Wk0yMCAyMC41QzE2LjQxMDEgMjAuNSAxMy41IDE3LjU4OTkgMTMuNSAxNEMxMy41IDEwLjQxMDEgMTYuNDEwMSA3LjUgMjAgNy41QzIzLjU4OTkgNy41IDI2LjUgMTAuNDEwMSAyNi41IDE0QzI2LjUgMTcuNTg5OSAyMy41ODk5IDIwLjUgMjAgMjAuNVoiIGZpbGw9IiM0ZjQ2ZTUiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9zdmc+',
      scaledSize: new window.google.maps.Size(40, 40),
      anchor: new window.google.maps.Point(20, 40)
    };
  };

  return (
    <div style={{ height }}>
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={zoom}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={onMapClick}
          options={{
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
          }}
        >
          {/* Pickup Marker */}
          {pickup && (
            <Marker
              position={{ lat: pickup.lat, lng: pickup.lng }}
              icon={getPickupIcon()}
              onClick={() => setSelectedMarker({ type: 'pickup', data: pickup })}
            />
          )}

          {/* Dropoff Marker */}
          {dropoff && (
            <Marker
              position={{ lat: dropoff.lat, lng: dropoff.lng }}
              icon={getDropoffIcon()}
              onClick={() => setSelectedMarker({ type: 'dropoff', data: dropoff })}
            />
          )}

          {/* Driver Marker */}
          {driverLocation && (
            <Marker
              position={
                Array.isArray(driverLocation) 
                  ? { lat: driverLocation[0], lng: driverLocation[1] }
                  : driverLocation
              }
              icon={getDriverIcon()}
              onClick={() => setSelectedMarker({ type: 'driver', data: driverLocation })}
            />
          )}

          {/* Additional Markers */}
          {markers.map((marker, idx) => (
            <Marker
              key={`${marker.lat}-${marker.lng}-${idx}`}
              position={{ lat: marker.lat, lng: marker.lng }}
              onClick={() => setSelectedMarker({ type: 'marker', data: marker })}
            />
          ))}

          {/* Route Polyline */}
          {showRoute && routeCoordinates.length > 0 && (
            <Polyline
              path={routeCoordinates}
              options={{
                strokeColor: '#4f46e5',
                strokeOpacity: 0.7,
                strokeWeight: 4,
              }}
            />
          )}

          {/* InfoWindow */}
          {selectedMarker && (
            <InfoWindow
              position={
                selectedMarker.type === 'driver'
                  ? (Array.isArray(selectedMarker.data) 
                      ? { lat: selectedMarker.data[0], lng: selectedMarker.data[1] }
                      : selectedMarker.data)
                  : { lat: selectedMarker.data.lat, lng: selectedMarker.data.lng }
              }
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div style={{ padding: '8px' }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 'bold' }}>
                  {selectedMarker.type === 'pickup' && 'Pickup Location'}
                  {selectedMarker.type === 'dropoff' && 'Dropoff Location'}
                  {selectedMarker.type === 'driver' && 'Driver Location'}
                  {selectedMarker.type === 'marker' && (selectedMarker.data.name || 'Location')}
                </h3>
                {selectedMarker.data.address && (
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                    {selectedMarker.data.address}
                  </p>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default GoogleMapComponent;
